<script setup>
import { ref, computed } from 'vue'
import { useForm, Head } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import CatalogPlaceholder from '@/Components/Public/CatalogPlaceholder.vue'
import BrandHero from '@/Components/Public/BrandHero.vue'
import ProductRail from '@/Components/Public/ProductRail.vue'
import StaticLocationMap from '@/Components/Public/StaticLocationMap.vue'
import { useCurrency } from '@/composables/useCurrency'

defineOptions({ layout: PublicLayout })

const { t } = useI18n()
const { formatMoney, formatDate } = useCurrency()
const props = defineProps({
  tour:      { type: Object, required: true },
  base_path: { type: String, default: '/tours' },
  related:   { type: Array, default: () => [] }
})

const activeIdx = ref(0)
const photos = props.tour.photos?.length ? props.tour.photos : []

// "Plan your trip" booking helper
const today = computed(() => new Date().toLocaleDateString('en-CA')) // YYYY-MM-DD, local
const chosenDate = ref('')
const adults = ref(1)
const children = ref(0)

const hasChildPrice = computed(() => (props.tour.price_child_cents || 0) > 0)
const totalCents = computed(() =>
  adults.value * (props.tour.price_adult_cents || 0) + children.value * (props.tour.price_child_cents || 0)
)
const totalDisplay = computed(() => formatMoney(totalCents.value))

function bump(which, delta) {
  if (which === 'adults') adults.value = Math.max(1, adults.value + delta)
  else children.value = Math.max(0, children.value + delta)
}

const guestsLabel = computed(() => {
  const a = adults.value === 1
    ? t('public.tours.booking.guests_adult', { n: 1 })
    : t('public.tours.booking.guests_adults', { n: adults.value })
  if (children.value <= 0) return a
  const c = children.value === 1
    ? t('public.tours.booking.guests_child', { n: 1 })
    : t('public.tours.booking.guests_children', { n: children.value })
  return `${a}, ${c}`
})

const bookingMessage = computed(() => {
  const base = { title: props.tour.title, guests: guestsLabel.value, total: totalDisplay.value }
  return chosenDate.value
    ? t('public.tours.booking.message', { ...base, date: formatDate(chosenDate.value) })
    : t('public.tours.booking.message_nodate', base)
})

// Rewrite the booking message into text-prefill chat URLs (those with a ?text=
// param); pass start-param links (Telegram) through unchanged.
const bookingIntents = computed(() => {
  const out = {}
  for (const [platform, url] of Object.entries(props.tour.contact_intents || {})) {
    try {
      const u = new URL(url)
      if (u.searchParams.has('text')) { u.searchParams.set('text', bookingMessage.value); out[platform] = u.toString() }
      else out[platform] = url
    } catch { out[platform] = url }
  }
  return out
})

// Nicely-cased platform display names for the booking buttons.
const PLATFORM_NAMES = { whatsapp: 'WhatsApp', line: 'LINE', telegram: 'Telegram', instagram: 'Instagram', messenger: 'Messenger', viber: 'Viber' }
function platformName(p) { return PLATFORM_NAMES[p] || (p.charAt(0).toUpperCase() + p.slice(1)) }

// One "Book via …" button per chat channel. Text-prefill channels (which carry
// the auto-filled date/guests/estimate message) come first; others (e.g.
// Telegram's start-param link) just open the chat so the customer can mention
// the date/total they see on the card.
const bookingCtas = computed(() => {
  const hasText = (url) => { try { return new URL(url).searchParams.has('text') } catch { return false } }
  return Object.entries(bookingIntents.value)
    .map(([platform, url]) => ({ platform, url, name: platformName(platform), text: hasText(url) }))
    .sort((a, b) => Number(b.text) - Number(a.text))
})

// Reviews
const showAllReviews = ref(false)
const visibleReviews = computed(() =>
  showAllReviews.value ? props.tour.reviews?.items : props.tour.reviews?.items?.slice(0, 5)
)

const reviewForm = useForm({ author_name: '', rating: 5, body: '', email: '', website: '' })
const reviewSubmitted = ref(false)

function submitReview() {
  reviewForm.transform(d => ({
    review: { author_name: d.author_name, rating: d.rating, body: d.body, email: d.email },
    website: d.website
  })).post(props.tour.reviews_path, {
    preserveScroll: true,
    onSuccess: () => { reviewSubmitted.value = true }
  })
}
</script>

<template>
  <Head>
    <title>{{ tour.title }} · {{ t('public.head_title') }}</title>
    <meta name="description" :content="tour.summary || tour.description">
  </Head>

  <BrandHero kind="tour" :eyebrow="t(`vendor_portal.tour.categories.${tour.category}`)" :title="tour.title">
    <template #top>
      <a :href="base_path" class="bhero-back">← {{ t('public.tours.back') }}</a>
    </template>
    <p v-if="tour.summary" class="bhero-lede">{{ tour.summary }}</p>
    <span v-if="tour.verified" class="bhero-verified">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3l7 3v5c0 4.5-3 7.6-7 9-4-1.4-7-4.5-7-9V6z" /><path d="M9 12l2 2 4-4" /></svg>{{ t('public.verified') }}
    </span>
    <span v-if="tour.featured" class="bhero-badge bhero-badge--featured">★ {{ t('public.tours.badges.featured') }}</span>
    <span v-if="tour.tour_type && tour.tour_type !== 'group'" class="bhero-badge">{{ t(`public.tours.badges.${tour.tour_type}`) }}</span>
    <ul v-if="tour.vibes?.length" class="bhero-vibes">
      <li v-for="v in tour.vibes" :key="v">{{ t(`public.tours.vibes.${v}`) }}</li>
    </ul>
    <p class="bhero-price"><span class="fw-value">{{ tour.price_adult }}</span> <span class="bhero-per">/ {{ Math.round(tour.duration_minutes / 60) }}h</span></p>
    <div v-if="tour.contact_intents && Object.keys(tour.contact_intents).length" class="bhero-ctas">
      <a
        v-for="(url, platform, idx) in tour.contact_intents"
        :key="platform"
        :class="['bhero-cta', idx === 0 ? 'bhero-cta--solid' : 'bhero-cta--ghost']"
        :href="url"
        target="_blank"
        rel="noopener"
      >{{ t('public.write_on', { platform: platform.charAt(0).toUpperCase() + platform.slice(1) }) }}</a>
    </div>
  </BrandHero>

  <section class="body">
    <div class="body__inner">
      <div class="gallery">
        <div class="gallery__main">
          <img v-if="photos[activeIdx]" :src="photos[activeIdx].full" :alt="tour.title" fetchpriority="high">
          <CatalogPlaceholder v-else kind="tour" :category="tour.category" />
        </div>
        <ul v-if="photos.length > 1" class="thumbs">
          <li v-for="(p, i) in photos" :key="p.thumb">
            <button :class="['thumb', i === activeIdx && 'is-active']" @click="activeIdx = i">
              <img :src="p.thumb" alt="" loading="lazy">
            </button>
          </li>
        </ul>
      </div>

      <div class="detail">
        <div class="detail__content">
          <section v-if="tour.highlights?.length" class="card">
            <h2 class="section-title">{{ t('public.tours.sections.highlights') }}</h2>
            <ul class="flist flist--star">
              <li v-for="(x, i) in tour.highlights" :key="i">{{ x }}</li>
            </ul>
          </section>

          <section v-if="tour.description" class="card">
            <h2 class="section-title">{{ t('public.tours.sections.about') }}</h2>
            <p class="prose">{{ tour.description }}</p>
          </section>

          <div v-if="tour.inclusions?.length || tour.not_included?.length" class="pair">
            <section v-if="tour.inclusions?.length" class="card">
              <h2 class="section-title">{{ t('public.tours.sections.included') }}</h2>
              <ul class="flist flist--yes"><li v-for="(x, i) in tour.inclusions" :key="i">{{ x }}</li></ul>
            </section>
            <section v-if="tour.not_included?.length" class="card">
              <h2 class="section-title">{{ t('public.tours.sections.not_included') }}</h2>
              <ul class="flist flist--no"><li v-for="(x, i) in tour.not_included" :key="i">{{ x }}</li></ul>
            </section>
          </div>

          <section v-if="tour.what_to_bring?.length" class="card">
            <h2 class="section-title">{{ t('public.tours.sections.what_to_bring') }}</h2>
            <ul class="flist"><li v-for="(x, i) in tour.what_to_bring" :key="i">{{ x }}</li></ul>
          </section>

          <section v-if="tour.know_before?.length" class="card">
            <h2 class="section-title">{{ t('public.tours.sections.know_before') }}</h2>
            <ul class="flist flist--info"><li v-for="(x, i) in tour.know_before" :key="i">{{ x }}</li></ul>
          </section>

          <section v-if="tour.meeting_point_address || (tour.meeting_point_lat && tour.meeting_point_lng)" class="card">
            <h2 class="section-title">{{ t('public.tours.sections.meeting_point') }}</h2>
            <StaticLocationMap
              v-if="tour.meeting_point_lat && tour.meeting_point_lng"
              :lat="tour.meeting_point_lat"
              :lng="tour.meeting_point_lng"
              :label="tour.title"
            />
            <p v-if="tour.meeting_point_address" class="meet__addr fw-value">{{ tour.meeting_point_address }}</p>
            <p v-if="tour.meeting_point_description" class="prose">{{ tour.meeting_point_description }}</p>
            <a
              v-if="tour.meeting_point_lat && tour.meeting_point_lng"
              class="meet__dir"
              :href="`https://www.google.com/maps/search/?api=1&query=${tour.meeting_point_lat},${tour.meeting_point_lng}`"
              target="_blank"
              rel="noopener"
            >{{ t('public.tours.get_directions') }} →</a>
          </section>

          <!-- Reviews section -->
          <section class="card">
            <h2 class="section-title">{{ t('public.tours.reviews.title') }}</h2>

            <!-- Approved reviews list -->
            <div v-if="tour.reviews?.count > 0" class="reviews__header">
              <span class="reviews__average fw-value">{{ Number(tour.reviews.average).toFixed(1) }}</span>
              <span class="reviews__stars" aria-hidden="true">
                <span v-for="n in 5" :key="n" class="review-star" :style="{ color: n <= Math.round(tour.reviews.average) ? 'var(--warn)' : 'var(--line)' }">★</span>
              </span>
              <span class="reviews__count">{{ t('public.tours.reviews.count', { n: tour.reviews.count }, tour.reviews.count) }}</span>
            </div>

            <p v-if="tour.reviews?.count === 0" class="reviews__empty prose">{{ t('public.tours.reviews.empty') }}</p>

            <ul v-if="tour.reviews?.count > 0" class="reviews__list">
              <li v-for="(item, i) in visibleReviews" :key="i" class="review-item">
                <div class="review-item__meta">
                  <span class="review-item__stars" aria-hidden="true">
                    <span v-for="n in 5" :key="n" class="review-star" :style="{ color: n <= item.rating ? 'var(--warn)' : 'var(--line)' }">★</span>
                  </span>
                  <span class="review-item__author fw-value">{{ item.author_name }}</span>
                  <span class="review-item__date">{{ formatDate(item.date) }}</span>
                </div>
                <p class="review-item__body prose">{{ item.body }}</p>
              </li>
            </ul>

            <button
              v-if="tour.reviews?.count > 5 && !showAllReviews"
              class="reviews__more"
              type="button"
              @click="showAllReviews = true"
            >{{ t('public.tours.reviews.show_more') }}</button>

            <!-- Write a review form -->
            <div class="reviews__form-wrap">
              <h3 class="reviews__form-title">{{ t('public.tours.reviews.write') }}</h3>

              <div v-if="reviewSubmitted" class="reviews__thanks">
                {{ t('public.tours.reviews.thanks') }}
              </div>

              <form v-else class="reviews__form" @submit.prevent="submitReview">
                <!-- Visually hidden honeypot -->
                <input
                  v-model="reviewForm.website"
                  type="text"
                  name="website"
                  tabindex="-1"
                  autocomplete="off"
                  class="reviews__honeypot"
                  aria-hidden="true"
                >

                <label class="review-field">
                  <span class="review-field__label">{{ t('public.tours.reviews.name') }}</span>
                  <input
                    v-model="reviewForm.author_name"
                    type="text"
                    class="review-field__input"
                    required
                    maxlength="80"
                  >
                </label>

                <div class="review-field">
                  <span class="review-field__label">{{ t('public.tours.reviews.rating') }}</span>
                  <div class="star-picker" role="radiogroup" :aria-label="t('public.tours.reviews.rating')">
                    <button
                      v-for="n in 5"
                      :key="n"
                      type="button"
                      class="star-picker__btn"
                      :aria-label="`${n} star${n > 1 ? 's' : ''}`"
                      :aria-pressed="reviewForm.rating === n"
                      :style="{ color: n <= reviewForm.rating ? 'var(--warn)' : 'var(--line)' }"
                      @click="reviewForm.rating = n"
                    >★</button>
                  </div>
                </div>

                <label class="review-field">
                  <span class="review-field__label">{{ t('public.tours.reviews.text') }}</span>
                  <textarea
                    v-model="reviewForm.body"
                    class="review-field__input review-field__textarea"
                    required
                    minlength="3"
                    maxlength="2000"
                    rows="4"
                  />
                </label>

                <label class="review-field">
                  <span class="review-field__label">{{ t('public.tours.reviews.email') }}</span>
                  <input
                    v-model="reviewForm.email"
                    type="email"
                    class="review-field__input"
                    autocomplete="email"
                  >
                </label>

                <button type="submit" class="reviews__submit" :disabled="reviewForm.processing">
                  {{ t('public.tours.reviews.submit') }}
                </button>
              </form>
            </div>
          </section>
        </div>

        <aside class="detail__aside">
          <div class="facts">
            <h2 class="section-title">{{ t('public.tours.sections.at_a_glance') }}</h2>
            <dl class="dl">
              <div v-if="tour.cancellation_policy === 'free'" class="dl__cancel">
                <dt>{{ t('public.tours.fields.cancellation') }}</dt>
                <dd>✓ {{ t('public.tours.cancellation.free', { hours: tour.cancellation_cutoff_hours }) }}</dd>
              </div>
              <div v-else-if="tour.cancellation_policy === 'non_refundable'">
                <dt>{{ t('public.tours.fields.cancellation') }}</dt>
                <dd>{{ t('public.tours.cancellation.non_refundable') }}</dd>
              </div>
              <div><dt>{{ t('public.tours.fields.duration') }}</dt><dd>{{ Math.round(tour.duration_minutes / 60) }}h</dd></div>
              <div><dt>{{ t('public.tours.fields.price_adult') }}</dt><dd class="dl__price">{{ tour.price_adult }}</dd></div>
              <div v-if="tour.price_child"><dt>{{ t('public.tours.fields.price_child') }}</dt><dd>{{ tour.price_child }}</dd></div>
              <div v-if="tour.max_group_size"><dt>{{ t('public.tours.fields.max_group_size') }}</dt><dd>{{ tour.max_group_size }}</dd></div>
              <div v-if="tour.languages_offered"><dt>{{ t('public.tours.fields.languages_offered') }}</dt><dd>{{ tour.languages_offered }}</dd></div>
            </dl>
          </div>

          <div class="trust-card">
            <h2 class="section-title">{{ t('public.tours.trust.title') }}</h2>
            <ul class="trust-list">
              <li v-if="tour.verified">{{ t('public.tours.trust.verified') }}</li>
              <li>{{ t('public.tours.trust.no_payment') }}</li>
              <li>{{ t('public.tours.trust.real_team') }}</li>
              <li>{{ t('public.tours.trust.local_support') }}</li>
            </ul>
          </div>

          <div class="plan-card">
            <h2 class="section-title">{{ t('public.tours.booking.title') }}</h2>
            <label class="plan-row">
              <span class="plan-label">{{ t('public.tours.booking.date') }}</span>
              <input v-model="chosenDate" type="date" :min="today" class="plan-date">
            </label>
            <div class="plan-row">
              <span class="plan-label">{{ t('public.tours.booking.adults') }}</span>
              <span class="stepper">
                <button type="button" class="stepper__btn" :aria-label="t('public.tours.booking.adults')" @click="bump('adults', -1)">−</button>
                <span class="stepper__n fw-value">{{ adults }}</span>
                <button type="button" class="stepper__btn" :aria-label="t('public.tours.booking.adults')" @click="bump('adults', 1)">+</button>
              </span>
            </div>
            <div v-if="hasChildPrice" class="plan-row">
              <span class="plan-label">{{ t('public.tours.booking.children') }}</span>
              <span class="stepper">
                <button type="button" class="stepper__btn" :aria-label="t('public.tours.booking.children')" @click="bump('children', -1)">−</button>
                <span class="stepper__n fw-value">{{ children }}</span>
                <button type="button" class="stepper__btn" :aria-label="t('public.tours.booking.children')" @click="bump('children', 1)">+</button>
              </span>
            </div>
            <p class="plan-total">{{ t('public.tours.booking.estimate_total') }} <span class="fw-value">≈ {{ totalDisplay }}</span></p>
            <p class="plan-caveat">{{ t('public.tours.booking.estimate_caveat') }}</p>
            <div v-if="bookingCtas.length" class="plan-ctas">
              <a
                v-for="(c, i) in bookingCtas"
                :key="c.platform"
                :href="c.url"
                target="_blank"
                rel="noopener"
                :class="['plan-cta', i === 0 ? 'plan-cta--solid' : 'plan-cta--ghost']"
              >{{ t('public.tours.booking.cta_via', { platform: c.name }) }}</a>
            </div>
          </div>
        </aside>
      </div>
    </div>
  </section>

  <section
    v-if="related.length"
    class="related"
  >
    <div class="related__inner">
      <ProductRail
        :title="t('public.tours.related')"
        kind="tour"
        :items="related"
        :item-base="base_path"
        :view-all-href="base_path"
      />
    </div>
  </section>
</template>

<style scoped>
.bhero-back { display: inline-block; margin-bottom: 1rem; color: inherit; opacity: 0.85; text-decoration: none; font-size: 0.95rem; }
.bhero-back:hover { opacity: 1; }
.bhero-lede { margin: 0.9rem 0 0; font-size: clamp(1.05rem, 2vw, 1.25rem); line-height: 1.5; max-width: 52ch; opacity: 0.94; }
.bhero-verified {
  display: inline-flex; align-items: center; gap: 0.35rem; margin: 1rem 0 0;
  padding: 0.35rem 0.75rem; border-radius: 999px; font-size: 0.85rem; font-weight: 700;
  background: color-mix(in oklch, currentColor 16%, transparent);
  border: 1px solid color-mix(in oklch, currentColor 35%, transparent);
}
.bhero-verified svg { width: 1rem; height: 1rem; }
.bhero-badge {
  display: inline-flex; align-items: center; margin: 0.5rem 0.4rem 0 0;
  padding: 0.3rem 0.7rem; border-radius: 999px; font-size: 0.82rem; font-weight: 700;
  background: color-mix(in oklch, currentColor 14%, transparent);
  border: 1px solid color-mix(in oklch, currentColor 30%, transparent);
}
.bhero-badge--featured {
  background: var(--accent); color: var(--accentInk); border-color: transparent;
}
.bhero-vibes { list-style: none; margin: 0.6rem 0 0; padding: 0; display: flex; flex-wrap: wrap; gap: 0.35rem; }
.bhero-vibes li {
  font-size: 0.8rem; color: var(--inkSoft);
  background: var(--surfaceAlt); border: 1px solid var(--line);
  border-radius: 999px; padding: 0.18rem 0.6rem;
}
.bhero-price { margin: 1.1rem 0 0; font-size: clamp(1.6rem, 3vw, 2.1rem); }
.bhero-per { font-size: 1rem; opacity: 0.85; }
.bhero-ctas { display: flex; flex-wrap: wrap; gap: 0.6rem; margin-top: 1.5rem; }
.bhero-cta {
  display: inline-flex; align-items: center; padding: 0.8rem 1.4rem; border-radius: 999px;
  font-family: var(--font-display, var(--display)); font-weight: 700; font-size: 1rem; text-decoration: none;
}
.bhero-cta--solid { background: var(--ink); color: #fff; }
.bhero-cta--ghost { background: color-mix(in oklch, #fff 14%, transparent); color: inherit; border: 1px solid color-mix(in oklch, #fff 45%, transparent); }

.body { background: var(--bg); }
.body__inner { max-width: 96rem; margin: 0 auto; padding: clamp(2rem, 4vw, 3rem) clamp(1.25rem, 4vw, 3rem) 4rem; }
.gallery__main { aspect-ratio: 4 / 3; background: var(--surfaceAlt); border-radius: 1rem; overflow: hidden; display: grid; place-items: center; }
.gallery__main img { width: 100%; height: 100%; object-fit: cover; }
.thumbs { list-style: none; padding: 0; margin: 0.75rem 0 0; display: flex; gap: 0.5rem; overflow-x: auto; }
.thumb { width: 5rem; height: 4rem; padding: 0; border: 2px solid transparent; border-radius: 0.5rem; overflow: hidden; cursor: pointer; background: var(--surfaceAlt); }
.thumb.is-active { border-color: var(--accent); }
.thumb img { width: 100%; height: 100%; object-fit: cover; }
.detail { display: grid; grid-template-columns: 1fr; gap: 1.5rem; margin-top: 1.5rem; }
@media (min-width: 880px) { .detail { grid-template-columns: 1.6fr 0.9fr; gap: 2rem; align-items: start; } }
.detail__content { display: grid; gap: 1rem; }
.pair { display: grid; grid-template-columns: 1fr; gap: 1rem; }
@media (min-width: 560px) { .pair { grid-template-columns: 1fr 1fr; } }
@media (min-width: 880px) { .detail__aside { position: sticky; top: 1.5rem; } }
.facts { background: var(--surface); border: 1px solid var(--line); border-radius: 1rem; padding: 1.4rem; }
.flist { list-style: none; margin: 0; padding: 0; display: grid; gap: 0.5rem; }
.flist li { position: relative; padding-left: 1.6rem; color: var(--inkSoft); line-height: 1.5; }
.flist li::before { position: absolute; left: 0; top: 0.05rem; font-weight: 700; }
/* Default marker first; the modifier rules below have equal specificity, so
   they win by later source order. A list with no modifier (What to bring)
   keeps the default dot. Keep this exact order. */
.flist li::before        { content: "•"; color: var(--accent); }
.flist--star li::before  { content: "★"; color: var(--accent); }
.flist--yes li::before   { content: "✓"; color: var(--ok); }
.flist--no li::before    { content: "✗"; color: var(--danger); }
.flist--info li::before  { content: "ℹ"; color: var(--second); }
.card { background: var(--surface); border: 1px solid var(--line); border-radius: 1rem; padding: 1.4rem; }
.section-title { font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted); margin: 0 0 0.75rem; }
.prose { color: var(--inkSoft); line-height: 1.6; white-space: pre-wrap; margin: 0; }
.prose-inline { white-space: pre-wrap; }
.dl { display: grid; gap: 0.55rem; margin: 0; }
.dl > div { display: grid; grid-template-columns: 9rem 1fr; gap: 0.5rem; align-items: baseline; }
.dl dt { color: var(--muted); font-size: 0.875rem; }
.dl dd { margin: 0; color: var(--ink); font-size: 1rem; }
.dl__price { color: var(--accent); font-weight: 700; }
.dl__cancel dd { color: var(--ok); font-weight: 600; }
.meet__addr { margin: 0.85rem 0 0.35rem; }
.meet__dir { display: inline-block; margin-top: 0.6rem; font-weight: 700; color: var(--accent); text-decoration: none; }
.meet__dir:hover { text-decoration: underline; }
.related { background: var(--surface); border-top: 1px solid var(--line); }
.related__inner { max-width: 96rem; margin: 0 auto; padding: clamp(2rem, 4vw, 3rem) clamp(1.25rem, 4vw, 3rem); }
.trust-card {
  margin-top: 1.25rem; padding: 1.25rem;
  background: var(--surface); border: 1px solid var(--line);
  border-radius: var(--radius-table, 0.75rem);
}
.trust-list { list-style: none; margin: 0.75rem 0 0; padding: 0; display: grid; gap: 0.6rem; }
.trust-list li {
  position: relative; padding-left: 1.6rem; color: var(--inkSoft); line-height: 1.4;
}
.trust-list li::before {
  content: "✓"; position: absolute; left: 0; top: 0;
  color: var(--ok); font-weight: 700;
}
.plan-card {
  margin-top: 1.25rem; padding: 1.25rem;
  background: var(--surface); border: 1px solid var(--line);
  border-radius: var(--radius-table, 0.75rem);
}
.plan-row { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-top: 0.85rem; }
.plan-label { color: var(--inkSoft); font-size: 0.95rem; }
.plan-date {
  border: 1px solid var(--line); border-radius: var(--radius-input, 0.5rem);
  background: var(--bg); color: var(--ink); padding: 0.4rem 0.6rem; font: inherit;
  color-scheme: light;
}
.stepper { display: inline-flex; align-items: center; gap: 0.5rem; }
.stepper__btn {
  width: 2rem; height: 2rem; border-radius: 999px;
  border: 1px solid var(--line); background: var(--surface); color: var(--ink);
  font-size: 1.1rem; line-height: 1; cursor: pointer; display: grid; place-items: center;
}
.stepper__btn:hover { border-color: var(--accent); color: var(--accent); }
.stepper__n { min-width: 1.5rem; text-align: center; }
.plan-total { margin: 1rem 0 0.2rem; font-size: 1.05rem; color: var(--ink); }
.plan-caveat { margin: 0; font-size: 0.8rem; color: var(--muted); }
.plan-ctas { margin-top: 1rem; display: grid; gap: 0.5rem; }
.plan-cta {
  display: block; text-align: center; padding: 0.7rem 1rem;
  border-radius: 999px; font-weight: 700; text-decoration: none;
}
.plan-cta--solid { background: var(--accent); color: var(--accentInk); }
.plan-cta--solid:hover { filter: brightness(1.05); }
.plan-cta--ghost { background: var(--surface); color: var(--ink); border: 1px solid var(--line); }
.plan-cta--ghost:hover { border-color: var(--accent); color: var(--accent); }

/* Reviews */
.reviews__header { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 1rem; }
.reviews__average { font-size: 1.4rem; }
.reviews__stars { display: inline-flex; gap: 0.1rem; font-size: 1.1rem; }
.reviews__count { color: var(--muted); font-size: 0.9rem; }
.review-star { transition: color 0.1s; }
.reviews__empty { margin: 0.5rem 0 1rem; }
.reviews__list { list-style: none; margin: 0 0 1rem; padding: 0; display: grid; gap: 1rem; }
.review-item { padding: 0.9rem; background: var(--surfaceAlt); border: 1px solid var(--line); border-radius: var(--radius-table, 0.75rem); }
.review-item__meta { display: flex; align-items: center; flex-wrap: wrap; gap: 0.4rem 0.75rem; margin-bottom: 0.4rem; }
.review-item__stars { display: inline-flex; gap: 0.05rem; font-size: 0.95rem; }
.review-item__author { font-size: 0.95rem; }
.review-item__date { font-size: 0.8rem; color: var(--muted); margin-left: auto; }
.review-item__body { margin: 0; font-size: 0.95rem; }
.reviews__more { margin-top: 0.25rem; background: none; border: 1px solid var(--line); border-radius: var(--radius-button, 999px); padding: 0.45rem 1rem; cursor: pointer; color: var(--ink); font: inherit; font-size: 0.9rem; }
.reviews__more:hover { border-color: var(--accent); color: var(--accent); }
.reviews__form-wrap { margin-top: 1.5rem; padding-top: 1.25rem; border-top: 1px solid var(--line); }
.reviews__form-title { font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted); margin: 0 0 1rem; }
.reviews__thanks { padding: 0.85rem 1rem; background: var(--surfaceAlt); border: 1px solid var(--line); border-radius: var(--radius-table, 0.75rem); color: var(--ok); font-weight: 600; }
.reviews__form { display: grid; gap: 0.85rem; }
.reviews__honeypot { position: absolute; left: -9999px; width: 1px; height: 1px; overflow: hidden; opacity: 0; pointer-events: none; }
.review-field { display: grid; gap: 0.3rem; }
.review-field__label { font-size: 0.875rem; color: var(--muted); }
.review-field__input { border: 1px solid var(--line); border-radius: var(--radius-input, 0.5rem); background: var(--bg); color: var(--ink); padding: 0.45rem 0.65rem; font: inherit; font-size: 0.95rem; width: 100%; box-sizing: border-box; }
.review-field__input:focus { outline: 2px solid var(--accent); outline-offset: 1px; }
.review-field__textarea { resize: vertical; min-height: 6rem; }
.star-picker { display: inline-flex; gap: 0.15rem; }
.star-picker__btn { background: none; border: none; font-size: 1.5rem; cursor: pointer; padding: 0.1rem 0.05rem; line-height: 1; transition: color 0.1s, transform 0.1s; }
.star-picker__btn:hover { transform: scale(1.15); }
.reviews__submit { align-self: start; padding: 0.6rem 1.4rem; background: var(--accent); color: var(--accentInk); border: none; border-radius: var(--radius-button, 999px); font: inherit; font-weight: 700; cursor: pointer; font-size: 0.95rem; }
.reviews__submit:hover:not(:disabled) { filter: brightness(1.05); }
.reviews__submit:disabled { opacity: 0.6; cursor: not-allowed; }
</style>
